import React from 'react';
import { Helmet } from 'react-helmet-async';
import SeoHead from '../../../shared/seo/SeoHead';
import { TasselsSeo } from '../seo/TasselsSeo';
import '../styles/pompoms-page.css';

const FAQ_ICON = (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm1 15h-2v-2h2zm0-4h-2V7h2z" />
    </svg>
);

const TOGGLE_ICON = (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
        <path d="M12 5v14M5 12h14" />
    </svg>
);

const PRODUCT_TYPES = [
    {
        emoji: '🌼',
        name: 'Pom Pom Top Tassels',
        desc: 'Our signature style — a handmade faux fur or wool pom pom atop a multi-strand silk or cotton tassel body. The pom pom crown adds volume and softness while the flowing tassel creates elegant movement.',
        tags: ['5 cm – 20 cm', '200+ Colors', 'Best Seller', 'Signature Style'],
    },
    {
        emoji: '🪡',
        name: 'Silk Thread Tassels',
        desc: 'Lustrous multi-strand silk thread tassels with a smooth, high-sheen finish. Ideal for sarees, lehengas, curtains, home décor, and luxury fashion accessories. Available with or without pom pom crown.',
        tags: ['4 cm – 18 cm', 'Silk Sheen', 'Bridal Range', 'Luxury Finish'],
    },
    {
        emoji: '🧶',
        name: 'Wool Pom Pom Tassels',
        desc: 'Classic handspun wool tassels topped with a matching wool pom pom — a timeless winter accessory style for scarves, bags, hats, and bohemian home décor.',
        tags: ['5 cm – 15 cm', 'Natural Fiber', 'Winter Range', 'Boho Style'],
    },
    {
        emoji: '✨',
        name: 'Metallic Tassels',
        desc: 'Gold, silver, rose gold, and bronze metallic lurex tassels with a pom pom accent top. Perfect for festive lehengas, curtain tiebacks, gift wrapping, and premium fashion collections.',
        tags: ['5 cm – 15 cm', 'Gold / Silver', 'Rose Gold', 'Festive & Bridal'],
    },
    {
        emoji: '🌺',
        name: 'Faux Fur Pom Pom Tassels',
        desc: 'Ultra-fluffy faux fur pom pom crowns paired with flowing tassel strands — 100% cruelty-free. A premium style for bag charms, jacket ties, boots, and high-fashion accessories.',
        tags: ['6 cm – 22 cm', 'Cruelty-Free', 'Luxury Fashion', 'Bag Charm'],
    },
    {
        emoji: '🎀',
        name: 'Multi-Strand Fringe Tassels',
        desc: 'Dense multi-strand tassels with a bundled pom pom head for garment fringe borders, curtain edges, cushion trims, and decorative home textile applications.',
        tags: ['3 cm – 12 cm', 'Trim Ready', 'Home Textile', 'Dense Fringe'],
    },
];

const SPECS = [
    { icon: '📏', label: 'Available Sizes', value: '3 cm – 25 cm (custom sizes on request)' },
    { icon: '🎨', label: 'Color Range', value: '200+ standard colors + Pantone custom matching' },
    { icon: '🧵', label: 'Materials', value: 'Silk, wool, faux fur, metallic lurex, cotton, acrylic' },
    { icon: '📦', label: 'Wholesale MOQ', value: 'From 50 pieces per color / style' },
    { icon: '🚢', label: 'Global Shipping', value: 'DHL, FedEx, Airmail — 50+ countries' },
    { icon: '⏱️', label: 'Lead Time', value: '7–21 business days depending on quantity' },
];

const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
        {
            '@type': 'Question',
            name: 'What are designer tassels?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Designer tassels are decorative ornaments made from bundled strands of thread, silk, wool, or faux fur tied at the top with a knot or pom pom crown. Arte Craft India\'s designer tassels feature a handmade pom pom at the top (the "crown") with flowing multi-strand strands below — giving them both volume and graceful movement. They are used for fashion accessories, garment trims, home décor, curtain tiebacks, and gift wrapping.',
            },
        },
        {
            '@type': 'Question',
            name: 'What is a pom pom tassel?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'A pom pom tassel combines two elements: a fluffy spherical pom pom at the top (the crown) and flowing tassel strands below. The pom pom adds softness and volume while the tassel strands create elegant drape and movement. Arte Craft India makes pom pom tassels with faux fur, wool, or silk crowns paired with silk, cotton, or acrylic tassel strands — all handmade in New Delhi.',
            },
        },
        {
            '@type': 'Question',
            name: 'What materials are used for designer tassels?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Arte Craft India uses premium materials for each tassel type: silk thread (lustrous, high-sheen finish for fashion and bridal), wool yarn (natural, warm texture for winter and boho styles), faux fur (ultra-fluffy, cruelty-free for luxury accessories), metallic lurex (gold, silver, rose gold for festive and decorative use), acrylic yarn (vibrant colors, budget-friendly for craft applications), and cotton thread (natural, breathable for baby and sensitive-skin products).',
            },
        },
        {
            '@type': 'Question',
            name: 'What sizes of designer tassels are available?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Our designer tassels range from 3 cm mini tassels to 25 cm large statement tassels. Standard sizes include 3 cm, 5 cm, 7 cm, 8 cm, 10 cm, 12 cm, 15 cm, 18 cm, and 20 cm. The tassel length is measured from the top of the pom pom crown to the tip of the longest strand. Custom sizes are available for wholesale orders with a minimum of 50 pieces.',
            },
        },
        {
            '@type': 'Question',
            name: 'Can I order designer tassels in custom colors?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes — custom color tassels are one of Arte Craft India\'s most requested services. We offer 200+ standard colors and full Pantone color matching for wholesale orders. Simply share your Pantone code, fabric swatch, or reference image; we produce a dye-match sample within 5–7 business days. After your approval, bulk production begins. Custom color tassels with pom pom tops are ideal for fashion brands that need accessories to match a specific seasonal palette.',
            },
        },
        {
            '@type': 'Question',
            name: 'What is the minimum order quantity (MOQ) for wholesale tassels?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'The wholesale minimum order quantity is 50 pieces per color and style for standard tassels. For custom Pantone colors or non-standard sizes, the MOQ is 100 pieces. Single pieces and small retail quantities are available with no minimum for in-stock colors. Volume price breaks are offered at 100, 250, 500, 1,000, and 5,000+ units. Private label and custom packaging are available for orders of 500+ pieces.',
            },
        },
        {
            '@type': 'Question',
            name: 'What are designer tassels used for?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Designer tassels and pom pom tassels are used for: bag charms and handbag zipper pulls, scarf and shawl edge accents, hat and beanie decorations, jacket and hoodie string ends, curtain and blind tiebacks, cushion and throw blanket trims, garment hem and neckline fringe, earrings and jewelry, gift wrapping and gift boxes, Christmas tree ornaments, wedding and event décor, and bookmark tassels.',
            },
        },
        {
            '@type': 'Question',
            name: 'How long does production take for a tassel order?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Standard in-stock tassels ship within 2–5 business days. Custom color or size tassel orders take 7–14 business days for small quantities (up to 500 pieces) and 3–5 weeks for large wholesale orders (1,000+ pieces). Rush production is available for an additional fee. All lead times begin after sample approval.',
            },
        },
        {
            '@type': 'Question',
            name: 'Do you ship designer tassels internationally?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes — Arte Craft India ships to 50+ countries worldwide via DHL Express, FedEx International, and Registered Airmail. All shipments include a tracking number. We regularly supply tassel buyers in the USA, UK, Canada, Australia, Germany, France, UAE, Saudi Arabia, Japan, South Korea, and across Europe and South-East Asia. Import duties and taxes are the responsibility of the buyer.',
            },
        },
        {
            '@type': 'Question',
            name: 'How do I care for faux fur pom pom tassels?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'For faux fur pom pom tassels: (1) Hand wash gently in cold water with mild detergent. (2) Do not machine wash, tumble dry, or iron directly. (3) Gently squeeze out excess water — do not wring. (4) Air dry in a well-ventilated area; reshape the pom pom crown while damp. (5) Once dry, fluff the pom pom with a soft bristle brush and straighten tassel strands by hand. (6) Store loosely — do not compress as sustained pressure flattens faux fur pile.',
            },
        },
        {
            '@type': 'Question',
            name: 'Are your faux fur tassels cruelty-free?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes — 100% cruelty-free. All faux fur pom pom tassels are made from premium synthetic acrylic and polyester fibres with no animal fur whatsoever. There is no real fur, no down, and no animal-derived materials of any kind in our faux fur tassel range. Our wool tassels use ethically sourced, mulesing-free natural wool. Material composition documentation is available on request for brands requiring certification.',
            },
        },
    ],
};

const productStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Designer Tassels — Handmade Pom Pom Tassels',
    description:
        'Premium handmade designer tassels with pom pom crowns manufactured in New Delhi, India. Available in silk, wool, faux fur, and metallic varieties. 200+ colors, custom orders, wholesale MOQ from 50 pieces. Ships to 50+ countries.',
    brand: { '@type': 'Brand', name: 'Arte Craft India' },
    manufacturer: {
        '@type': 'Organization',
        name: 'Arte Craft India',
        url: 'https://www.artecraftindia.com',
        telephone: '+91-9818548369',
        email: 'info@artecraftindia.com',
        address: {
            '@type': 'PostalAddress',
            streetAddress: '280/13 Gali No. 29 Molarband Extn.',
            addressLocality: 'Badarpur, New Delhi',
            addressRegion: 'Delhi',
            postalCode: '110044',
            addressCountry: 'IN',
        },
        foundingDate: '2017',
    },
    offers: {
        '@type': 'AggregateOffer',
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        seller: { '@type': 'Organization', name: 'Arte Craft India' },
    },
    material: 'Silk Thread, Wool Yarn, Faux Fur, Metallic Lurex, Cotton, Acrylic',
    color: '200+ colors available',
    countryOfOrigin: 'India',
};

const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.artecraftindia.com' },
        { '@type': 'ListItem', position: 2, name: 'Products', item: 'https://www.artecraftindia.com/products' },
        { '@type': 'ListItem', position: 3, name: 'Tassels', item: 'https://www.artecraftindia.com/products/tassels' },
    ],
};

const webPageStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Designer Tassels — Handmade Pom Pom Tassels | Arte Craft India',
    description:
        'Explore Arte Craft India\'s full range of handmade designer tassels with pom pom crowns. Silk, wool, faux fur and metallic tassels in 200+ colors. Wholesale & custom orders from New Delhi.',
    url: 'https://www.artecraftindia.com/products/tassels',
    inLanguage: 'en-US',
    publisher: {
        '@type': 'Organization',
        name: 'Arte Craft India',
        url: 'https://www.artecraftindia.com',
        logo: {
            '@type': 'ImageObject',
            url: 'https://www.artecraftindia.com/images/logo.jpg',
        },
    },
    speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['#tassels-hero-h1', '#tassels-about-h2', '#tassels-faq-h2'],
    },
};

const organizationStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Arte Craft India',
    url: 'https://www.artecraftindia.com',
    logo: 'https://www.artecraftindia.com/images/logo.jpg',
    telephone: '+91-9818548369',
    email: 'info@artecraftindia.com',
    foundingDate: '2017',
    address: {
        '@type': 'PostalAddress',
        streetAddress: '280/13 Gali No. 29 Molarband Extn.',
        addressLocality: 'Badarpur, New Delhi',
        addressRegion: 'Delhi',
        postalCode: '110044',
        addressCountry: 'IN',
    },
    sameAs: [
        'https://www.instagram.com/artecraftindia',
        'https://www.facebook.com/artecraftindia',
    ],
    description:
        'Arte Craft India is a direct manufacturer of handmade pom poms, designer tassels, fringes, and textile accessories based in New Delhi, India. Established in 2017, supplying wholesale buyers in 50+ countries.',
};

const DesignerTasselsPage: React.FC = () => {
    return (
        <div className="pp-page">

            <SeoHead {...TasselsSeo} />

            <Helmet>
                <script type="application/ld+json">{JSON.stringify(faqStructuredData)}</script>
                <script type="application/ld+json">{JSON.stringify(productStructuredData)}</script>
                <script type="application/ld+json">{JSON.stringify(breadcrumbStructuredData)}</script>
                <script type="application/ld+json">{JSON.stringify(webPageStructuredData)}</script>
                <script type="application/ld+json">{JSON.stringify(organizationStructuredData)}</script>
            </Helmet>

            {/* ── BREADCRUMB ── */}
            <nav aria-label="Breadcrumb" className="pp-breadcrumb-nav">
                <ol
                    className="pp-breadcrumb"
                    itemScope
                    itemType="https://schema.org/BreadcrumbList"
                >
                    <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                        <a href="/" itemProp="item"><span itemProp="name">Home</span></a>
                        <meta itemProp="position" content="1" />
                    </li>
                    <li><span className="pp-bsep" aria-hidden="true">›</span></li>
                    <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                        <a href="/products" itemProp="item"><span itemProp="name">Products</span></a>
                        <meta itemProp="position" content="2" />
                    </li>
                    <li><span className="pp-bsep" aria-hidden="true">›</span></li>
                    <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                        <span itemProp="name" aria-current="page">Tassels</span>
                        <meta itemProp="position" content="3" />
                    </li>
                </ol>
            </nav>

            {/* ── HERO ── */}
            <section className="pp-hero" aria-labelledby="tassels-hero-h1">
                <div className="pp-hero-dots" aria-hidden="true">
                    {Array.from({ length: 12 }).map((_, i) => <span key={i} />)}
                </div>
                <div className="pp-hero-inner">
                    <div className="pp-hero-badge" aria-hidden="true">
                        Manufacturer Direct · Est. 2017 · New Delhi, India
                    </div>
                    <h1 id="tassels-hero-h1">
                        Handmade{' '}
                        <span className="pp-hero-accent">Designer Tassels</span>
                        <br />
                        Pom Pom Crowns · Custom Colors · Wholesale
                    </h1>
                    <p className="pp-hero-desc speakable-summary">
                        Direct-from-manufacturer designer tassels with pom pom tops in silk, wool, faux fur &amp; metallic.
                        200+ colors, sizes 3 cm – 25 cm, cruelty-free options. Custom orders and bulk wholesale
                        supplied to 50+ countries since 2017.
                    </p>
                    <div className="pp-hero-stats" role="list" aria-label="Key product highlights">
                        <div className="pp-stat" role="listitem">
                            <span className="pp-stat-num">200+</span>
                            <span className="pp-stat-label">Colors</span>
                        </div>
                        <div className="pp-stat" role="listitem">
                            <span className="pp-stat-num">3–25cm</span>
                            <span className="pp-stat-label">Sizes</span>
                        </div>
                        <div className="pp-stat" role="listitem">
                            <span className="pp-stat-num">50+</span>
                            <span className="pp-stat-label">Countries</span>
                        </div>
                        <div className="pp-stat" role="listitem">
                            <span className="pp-stat-num">100%</span>
                            <span className="pp-stat-label">Handmade</span>
                        </div>
                    </div>
                    <div className="pp-hero-cta">
                        <a href="/request-a-quote" className="pp-btn pp-btn-gold">
                            Request a Wholesale Quote
                        </a>
                        <a href="/contact" className="pp-btn pp-btn-outline">
                            Contact Us
                        </a>
                    </div>
                </div>
            </section>

            {/* ── TRUST STRIP ── */}
            <div className="pp-trust-strip" aria-label="Why buy from Arte Craft India">
                <div className="pp-trust-inner">
                    <div className="pp-trust-item">
                        <div className="pp-trust-icon" aria-hidden="true">🤲</div>
                        <div className="pp-trust-text">
                            <strong>100% Handmade</strong>
                            <span>Every tassel hand-crafted</span>
                        </div>
                    </div>
                    <div className="pp-trust-item">
                        <div className="pp-trust-icon" aria-hidden="true">🎨</div>
                        <div className="pp-trust-text">
                            <strong>Custom Colors</strong>
                            <span>Pantone matching available</span>
                        </div>
                    </div>
                    <div className="pp-trust-item">
                        <div className="pp-trust-icon" aria-hidden="true">📦</div>
                        <div className="pp-trust-text">
                            <strong>Wholesale MOQ 50 pcs</strong>
                            <span>Tiered volume pricing</span>
                        </div>
                    </div>
                    <div className="pp-trust-item">
                        <div className="pp-trust-icon" aria-hidden="true">🌍</div>
                        <div className="pp-trust-text">
                            <strong>Ships Worldwide</strong>
                            <span>DHL, FedEx, Airmail</span>
                        </div>
                    </div>
                    <div className="pp-trust-item">
                        <div className="pp-trust-icon" aria-hidden="true">🐇</div>
                        <div className="pp-trust-text">
                            <strong>Cruelty-Free Faux Fur</strong>
                            <span>100% animal-free options</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── PRODUCT TYPES ── */}
            <main id="main-content" role="main">
                <div className="pp-wrapper">

                    <div className="pp-section-header">
                        <span className="pp-section-label">Our Range</span>
                        <h2>Designer Tassel Collections</h2>
                        <p className="pp-section-sub">
                            Six distinct pom pom tassel styles — each crafted for a specific use case,
                            all available in custom colors and wholesale quantities.
                        </p>
                    </div>

                    <div
                        className="pp-types-grid"
                        itemScope
                        itemType="https://schema.org/ItemList"
                        aria-label="Designer tassel product collections"
                    >
                        {PRODUCT_TYPES.map((pt, index) => (
                            <article
                                key={pt.name}
                                className="pp-type-card"
                                itemScope
                                itemProp="itemListElement"
                                itemType="https://schema.org/ListItem"
                            >
                                <meta itemProp="position" content={String(index + 1)} />
                                <span className="pp-type-emoji" aria-hidden="true">{pt.emoji}</span>
                                <h3 itemProp="name">{pt.name}</h3>
                                <p itemProp="description">{pt.desc}</p>
                                <div className="pp-type-tags" aria-label={`${pt.name} specifications`}>
                                    {pt.tags.map(t => <span key={t} className="pp-tag">{t}</span>)}
                                </div>
                            </article>
                        ))}
                    </div>

                    {/* ── ABOUT / SPECIFICATIONS ── */}
                    <section className="pp-about-grid" aria-labelledby="tassels-about-h2">
                        <div className="pp-about-prose">
                            <h2 id="tassels-about-h2">
                                India's Premier Handmade Tassel &amp; Pom Pom Manufacturer
                            </h2>
                            <p>
                                <strong>Arte Craft India</strong> has been producing handmade designer tassels and pom pom
                                accessories from New Delhi since 2017. We are a <strong>direct manufacturer</strong> — not
                                a middleman — which means every tassel you receive is handcrafted in our own workshop by
                                skilled artisans, giving you factory-direct quality at the best possible price.
                            </p>
                            <p>
                                Our designer tassels feature a distinctive <strong>pom pom crown</strong> combined with
                                flowing multi-strand tassel bodies. This signature construction gives our tassels
                                exceptional volume at the top and graceful drape in the strands — making them a popular
                                choice for luxury fashion brands, ethnic wear designers, home textile manufacturers,
                                and accessories retailers worldwide.
                            </p>
                            <p>
                                We supply tassel buyers across the <strong>USA, UK, Canada, Australia, UAE, Europe, and
                                South-East Asia</strong>, handling everything from single-piece retail orders to large-volume
                                wholesale contracts of 10,000+ units with private label packaging.
                            </p>
                        </div>
                        <div className="pp-specs-list" aria-label="Product specifications">
                            {SPECS.map(s => (
                                <div key={s.label} className="pp-spec-row">
                                    <div className="pp-spec-icon" aria-hidden="true">{s.icon}</div>
                                    <div className="pp-spec-text">
                                        <strong>{s.label}</strong>
                                        <span>{s.value}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* ── FAQ ── */}
                    <section aria-labelledby="tassels-faq-h2" itemScope itemType="https://schema.org/FAQPage">
                        <div className="pp-section-header" style={{ paddingTop: 0 }}>
                            <span className="pp-section-label">Knowledge Base</span>
                            <h2 id="tassels-faq-h2">Frequently Asked Questions — Designer Tassels</h2>
                            <p className="pp-section-sub">
                                Everything you need to know before placing your order — from materials and
                                care to wholesale pricing and international shipping.
                            </p>
                        </div>

                        {/* ── About Designer Tassels ── */}
                        <h3 className="pp-cat-heading">
                            <span className="pp-cat-dot" aria-hidden="true" />
                            About Designer Tassels &amp; Pom Pom Tassels
                        </h3>
                        <div className="pp-faq-grid speakable-faq">

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">What are designer tassels?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Definition</span>
                                        <p itemProp="text">
                                            <strong>Designer tassels</strong> are decorative ornaments made from bundled strands of thread,
                                            silk, wool, or faux fur tied at the top with a knot or pom pom crown. Arte Craft India's
                                            designer tassels feature a handmade pom pom at the top (the "crown") with flowing multi-strand
                                            strands below — giving them both volume and graceful movement.
                                        </p>
                                        <p>
                                            They are used for fashion accessories, garment trims, home décor, curtain tiebacks, bag charms,
                                            and gift wrapping. The combination of a pom pom crown with a tassel body is our signature style,
                                            crafted to order by skilled artisans in New Delhi since 2017.
                                        </p>
                                    </div>
                                </details>
                            </article>

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">What is a pom pom tassel and how is it different from a regular tassel?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Comparison</span>
                                        <p itemProp="text">
                                            A <strong>regular tassel</strong> has a simple gathered or knotted top with flowing strands below.
                                            A <strong>pom pom tassel</strong> adds a fluffy spherical pom pom as the crown — this makes the
                                            top broader, more three-dimensional, and visually striking while the tassel strands provide the
                                            characteristic flowing movement.
                                        </p>
                                        <p>
                                            The pom pom crown can be made from faux fur (ultra-fluffy), wool (natural texture), or acrylic
                                            (vibrant and lightweight). The result is a versatile accessory that works equally well on luxury
                                            handbags, bohemian scarves, ethnic garments, and contemporary home décor.
                                        </p>
                                    </div>
                                </details>
                            </article>

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">What materials are used for designer tassels?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Materials</span>
                                        <p itemProp="text">We use carefully selected materials for each tassel type:</p>
                                        <ul>
                                            <li><strong>Silk Thread</strong> — Lustrous, high-sheen finish; ideal for bridal, saree, and luxury accessories</li>
                                            <li><strong>Wool Yarn</strong> — Natural, warm, matte texture; suited for winter wear and boho styles</li>
                                            <li><strong>Faux Fur</strong> — Ultra-fluffy synthetic pile for the pom pom crown; 100% cruelty-free</li>
                                            <li><strong>Metallic Lurex</strong> — Woven metallic filament for gold, silver, and rose gold effects</li>
                                            <li><strong>Acrylic Yarn</strong> — Budget-friendly, vibrant color retention, lightweight; great for craft applications</li>
                                            <li><strong>Cotton Thread</strong> — Natural, breathable; ideal for children's accessories and sensitive skin</li>
                                        </ul>
                                    </div>
                                </details>
                            </article>

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">What sizes of designer tassels are available?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Sizes</span>
                                        <p itemProp="text">
                                            Our designer tassels range from <strong>3 cm mini tassels</strong> to <strong>25 cm large statement tassels</strong>.
                                            Standard stocked sizes are: <strong>3 cm, 5 cm, 7 cm, 8 cm, 10 cm, 12 cm, 15 cm, 18 cm</strong>, and
                                            <strong> 20 cm</strong>. The length is measured from the top of the pom pom crown to the tip of
                                            the longest tassel strand.
                                        </p>
                                        <p>
                                            Popular size guidance: <strong>5–8 cm</strong> for earrings and gift wrapping;
                                            <strong> 8–12 cm</strong> for bag charms and zipper pulls; <strong>12–18 cm</strong> for garment
                                            trims and curtain tiebacks; <strong>18–25 cm</strong> for statement fashion and home décor.
                                            Custom sizes are available for wholesale orders.
                                        </p>
                                    </div>
                                </details>
                            </article>

                        </div>

                        {/* ── Uses & Applications ── */}
                        <h3 className="pp-cat-heading">
                            <span className="pp-cat-dot" aria-hidden="true" />
                            Uses &amp; Applications
                        </h3>
                        <div className="pp-faq-grid speakable-faq">

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">What are designer tassels used for?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Applications</span>
                                        <p itemProp="text">Designer tassels with pom pom crowns are among the most versatile fashion and home décor accessories. Common uses include:</p>
                                        <ul>
                                            <li><strong>Bag charms &amp; zipper pulls</strong> — faux fur and silk pom pom tassels on luxury handbags</li>
                                            <li><strong>Garment trims</strong> — rows of tassels along hems, necklines, dupattas, and lehengas</li>
                                            <li><strong>Earrings &amp; jewelry</strong> — lightweight mini pom pom tassels as statement drops</li>
                                            <li><strong>Scarf &amp; shawl accents</strong> — tassel fringe along the edges of shawls and wraps</li>
                                            <li><strong>Curtain tiebacks</strong> — large tassels to hold and decorate curtains and drapes</li>
                                            <li><strong>Cushion &amp; throw trim</strong> — tassel fringe borders on home textiles</li>
                                            <li><strong>Hat &amp; beanie accessories</strong> — tassels and pom pom tassel combinations on winter hats</li>
                                            <li><strong>Gift wrapping &amp; stationery</strong> — elegant tassel toppers on gift boxes and bookmarks</li>
                                            <li><strong>Wedding &amp; event décor</strong> — custom color tassels for mandap, venue, and bridal accessories</li>
                                        </ul>
                                    </div>
                                </details>
                            </article>

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">Are designer tassels suitable for ethnic Indian fashion?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Ethnic Fashion</span>
                                        <p itemProp="text">
                                            <strong>Yes — tassels are a cornerstone of Indian ethnic fashion.</strong> They are traditionally
                                            used on sarees, lehengas, dupattas, kurtas, and bridal wear. Arte Craft India's silk thread
                                            tassels and metallic lurex tassels are particularly popular for bridal and festive Indian
                                            garment collections.
                                        </p>
                                        <p>
                                            Our pom pom tassels add a contemporary boho twist to ethnic silhouettes — increasingly popular
                                            in designer kurtas, palazzo sets, and fusion fashion. We work with Indian fashion houses,
                                            export garment manufacturers, and boutique designers to supply custom color tassels that match
                                            specific fabric colorways and seasonal palettes.
                                        </p>
                                    </div>
                                </details>
                            </article>

                        </div>

                        {/* ── Wholesale & Custom Orders ── */}
                        <h3 className="pp-cat-heading">
                            <span className="pp-cat-dot" aria-hidden="true" />
                            Wholesale &amp; Custom Orders
                        </h3>
                        <div className="pp-faq-grid speakable-faq">

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">What is the minimum order quantity (MOQ) for wholesale tassels?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">MOQ &amp; Pricing</span>
                                        <p itemProp="text">
                                            For <strong>in-stock standard colors</strong>, there is no minimum — you can order a single tassel.
                                            For <strong>custom colors or non-standard sizes</strong>, the MOQ is <strong>50 pieces per style</strong>.
                                            For <strong>Pantone-matched custom dye lots</strong>, the MOQ is <strong>100 pieces per color</strong>.
                                        </p>
                                        <p>
                                            Wholesale volume price breaks are offered at <strong>100, 250, 500, 1,000, and 5,000+ units</strong>.
                                            Private label and custom packaging are available for orders of 500+ pieces.
                                            Contact us for a full pricing sheet with tiered wholesale rates.
                                        </p>
                                    </div>
                                </details>
                            </article>

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">Can I order designer tassels in custom colors?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Custom Colors</span>
                                        <p itemProp="text">
                                            <strong>Yes — custom color tassels are one of our most requested services.</strong> We offer
                                            200+ standard colors and full Pantone color matching for wholesale orders. To order custom color tassels:
                                        </p>
                                        <ol>
                                            <li>Share your <strong>Pantone code, fabric swatch, or reference image</strong></li>
                                            <li>We produce a <strong>dye-match sample</strong> within 5–7 business days</li>
                                            <li>You approve the sample and confirm the order</li>
                                            <li>Bulk production begins after your written approval</li>
                                        </ol>
                                        <p>
                                            Custom color tassels are ideal for fashion brands that need their accessories to match a
                                            specific season palette or garment colorway. Both the pom pom crown and the tassel strands
                                            can be matched to the same or contrasting colors.
                                        </p>
                                    </div>
                                </details>
                            </article>

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">How long does production take for a tassel order?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Lead Times</span>
                                        <p itemProp="text">Production timelines vary by order size and type:</p>
                                        <ul>
                                            <li><strong>In-stock standard tassels:</strong> Ships in 2–5 business days</li>
                                            <li><strong>Custom color / size (up to 200 pcs):</strong> 7–14 business days</li>
                                            <li><strong>Medium wholesale (200–1,000 pcs):</strong> 3–4 weeks</li>
                                            <li><strong>Large wholesale (1,000–10,000 pcs):</strong> 4–8 weeks</li>
                                            <li><strong>Rush production:</strong> Available for small orders — contact us with your deadline</li>
                                        </ul>
                                        <p>All lead times begin <strong>after sample approval</strong>. We can accommodate seasonal rush
                                        deadlines — please enquire at least 4 weeks in advance for large holiday-season orders.</p>
                                    </div>
                                </details>
                            </article>

                        </div>

                        {/* ── Care & Shipping ── */}
                        <h3 className="pp-cat-heading">
                            <span className="pp-cat-dot" aria-hidden="true" />
                            Care, Shipping &amp; After-Sales
                        </h3>
                        <div className="pp-faq-grid speakable-faq">

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">How do I care for faux fur pom pom tassels?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Care Instructions</span>
                                        <p itemProp="text">Follow these steps to keep faux fur pom pom tassels looking full and beautiful:</p>
                                        <ol>
                                            <li><strong>Hand wash only</strong> — gently in cold water (max 30°C) with mild detergent</li>
                                            <li><strong>Do not machine wash</strong>, tumble dry, or iron directly on the fur or silk strands</li>
                                            <li>Gently squeeze out excess water — do not wring or twist the tassel strands</li>
                                            <li><strong>Air dry</strong> in a well-ventilated area; reshape the pom pom crown while damp</li>
                                            <li>Once dry, <strong>fluff the pom pom</strong> with a soft bristle brush and straighten tassel strands by hand</li>
                                            <li>Store loosely — <strong>do not compress</strong> as sustained pressure flattens the faux fur pile</li>
                                        </ol>
                                    </div>
                                </details>
                            </article>

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">Do you ship designer tassels internationally?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">International Shipping</span>
                                        <p itemProp="text">
                                            <strong>Yes — we ship handmade designer tassels to 50+ countries worldwide.</strong> All orders
                                            include a tracking number. Available shipping options:
                                        </p>
                                        <ul>
                                            <li><strong>DHL Express</strong> — fastest, typically 3–5 business days internationally</li>
                                            <li><strong>FedEx International</strong> — reliable, 4–7 business days</li>
                                            <li><strong>Registered Airmail</strong> — economy option, 10–21 business days</li>
                                        </ul>
                                        <p>
                                            We regularly supply buyers in the <strong>USA, UK, Canada, Australia, Germany, France, Netherlands,
                                            UAE, Saudi Arabia, Japan, South Korea</strong>, and across Europe and South-East Asia.
                                            Import duties and taxes are the responsibility of the buyer.
                                        </p>
                                    </div>
                                </details>
                            </article>

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">Are your faux fur tassels cruelty-free and vegan?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Ethics &amp; Sustainability</span>
                                        <p itemProp="text">
                                            <strong>Yes — all faux fur pom pom tassels are 100% cruelty-free and vegan.</strong> They are
                                            made entirely from synthetic acrylic and polyester fibres with zero animal content. There is no
                                            real fur, no down, and no animal-derived materials of any kind in our faux fur tassel range.
                                        </p>
                                        <p>
                                            Our wool tassels use ethically sourced natural wool from mulesing-free certified suppliers.
                                            If strict vegan certification is required for your brand, we recommend the faux fur or acrylic
                                            yarn range and can provide material composition documentation on request.
                                        </p>
                                    </div>
                                </details>
                            </article>

                        </div>
                    </section>

                    {/* ── RELATED PRODUCTS LINK ── */}
                    <section className="pp-about-grid" style={{ borderTop: '1px solid var(--pp-border)', paddingTop: 'clamp(40px, 6vw, 72px)' }} aria-labelledby="tassels-related-h2">
                        <div className="pp-about-prose">
                            <h2 id="tassels-related-h2">Related Pom Pom Products</h2>
                            <p>
                                All our designer tassels share their <strong>pom pom crown technology</strong> with our
                                standalone pom pom range. If you need just the pom pom component without the tassel
                                strands — for hat toppers, bag charms, or garment accessories — explore our dedicated
                                pom pom collections.
                            </p>
                            <p>
                                We also supply <strong>designer fringes</strong>, <strong>beaded patches</strong>, and
                                <strong> beaded necklines</strong> — all handmade in our New Delhi workshop. Wholesale
                                buyers frequently combine multiple product lines into a single shipment to reduce
                                freight costs.
                            </p>
                            <div className="pp-hero-cta" style={{ justifyContent: 'flex-start', marginTop: '20px' }}>
                                <a href="/products/pom-poms" className="pp-btn pp-btn-gold">
                                    View Pom Pom Range
                                </a>
                                <a href="/products" className="pp-btn pp-btn-outline" style={{ color: 'var(--pp-warm-brown)', borderColor: 'var(--pp-border-strong)' }}>
                                    All Products
                                </a>
                            </div>
                        </div>
                        <div className="pp-specs-list" aria-label="Related product categories">
                            {[
                                { icon: '🌺', label: 'Faux Fur Pom Poms', value: 'Standalone pom poms for hats, bags & accessories' },
                                { icon: '🧶', label: 'Wool Pom Poms', value: 'Classic handspun wool pom poms, 1–25 cm' },
                                { icon: '✨', label: 'Metallic Pom Poms', value: 'Gold, silver & rose gold festive pom poms' },
                                { icon: '🪡', label: 'Designer Fringes', value: 'Tassel fringe trim for garments & home textiles' },
                                { icon: '💎', label: 'Beaded Patches', value: 'Hand-embroidered beaded appliqués & patches' },
                            ].map(s => (
                                <div key={s.label} className="pp-spec-row">
                                    <div className="pp-spec-icon" aria-hidden="true">{s.icon}</div>
                                    <div className="pp-spec-text">
                                        <strong>{s.label}</strong>
                                        <span>{s.value}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* ── CTA ── */}
                    <section className="pp-cta-banner" aria-labelledby="tassels-cta-h2">
                        <h2 id="tassels-cta-h2">Ready to Order Handmade Designer Tassels?</h2>
                        <p>
                            Get a free wholesale quote within 24 hours. Custom colors, pom pom crowns,
                            bulk orders, and private label packaging available. Trusted by brands in 50+ countries.
                        </p>
                        <div className="pp-cta-buttons">
                            <a href="/request-a-quote" className="pp-btn pp-btn-gold">
                                Request a Wholesale Quote
                            </a>
                            <a href="/contact" className="pp-btn pp-btn-outline">
                                Ask a Question
                            </a>
                        </div>
                    </section>

                </div>
            </main>
        </div>
    );
};

export default DesignerTasselsPage;
